import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo } from '@/mocks/arina';

const aboutBreadcrumb = [
  { label: '首页', path: '/' },
  { label: '关于我们' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypot && honeypot.value.trim()) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      setFormData({ name: '', email: '', message: '' });
      return;
    }
    const fd = new FormData(form);
    try {
      const res = await fetch('https://readdy.ai/api/form/d9sr1ed8o1vn02ekukog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || text;
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后重试。');
        } else {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后重试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络后重试。');
    }
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '桥本有菜官方资料站',
    url: 'https://hashimoto-arina.com',
    description: aboutInfo.brandIntro,
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      email: aboutInfo.contact.email,
      contactType: 'customer service',
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: aboutInfo.contact.address,
      addressCountry: 'JP',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '桥本有菜官方资料站是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '桥本有菜官方资料站是华语地区最全面的桥本有菜资讯平台，提供个人资料、作品资讯、写真集、最新动态等信息服务。',
        },
      },
      {
        '@type': 'Question',
        name: '如何获取桥本有菜的最新动态？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过本站订阅最新资讯，或关注"最新动态"分类页面，我们会第一时间更新桥本有菜的相关信息。',
        },
      },
      {
        '@type': 'Question',
        name: '桥本有菜有哪些代表作品？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '桥本有菜的代表作品包括《花与少女》《东京恋歌》《星空下的约定》等影视作品，以及《花季》《夏の記憶》等写真集。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系桥本有菜官方资料站？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过本站的联系表单发送留言，或发送邮件至 contact@hashimoto-arina.com，我们会尽快回复。',
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-20">
          <div className="w-full max-w-5xl mx-auto px-6">
            <Breadcrumb items={aboutBreadcrumb} />

            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mt-6 mb-16 text-center">
              关于我们
            </h1>

            {/* Brand Introduction */}
            <section className="mb-20">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-2xl font-heading font-semibold text-foreground-900 mb-5">
                    品牌介绍
                  </h2>
                  <p className="text-base text-foreground-600 leading-relaxed mb-5">
                    {aboutInfo.brandIntro}
                  </p>
                  <p className="text-base text-foreground-600 leading-relaxed">
                    {aboutInfo.mission}
                  </p>
                </div>
                <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20office%20workspace%20with%20warm%20cream%20and%20beige%20tones%2C%20Japanese%20aesthetic%2C%20soft%20natural%20lighting%2C%20clean%20modern%20design%2C%20cozy%20atmosphere%2C%20professional%20editorial%20photography&width=800&height=600&seq=about-brand-01&orientation=landscape"
                    alt="桥本有菜官方资料站"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </section>

            {/* Reputation Stats */}
            <section className="mb-20">
              <h2 className="text-2xl font-heading font-semibold text-foreground-900 mb-10 text-center">
                声誉与数据
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                {aboutInfo.reputation.map((item) => (
                  <div
                    key={item.label}
                    className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70"
                  >
                    <div className="text-3xl md:text-4xl font-heading font-bold text-primary-600 mb-2">
                      {item.value}
                    </div>
                    <div className="text-sm text-foreground-500">{item.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section className="mb-20">
              <h2 className="text-2xl font-heading font-semibold text-foreground-900 mb-10 text-center">
                常见问题
              </h2>
              <div className="max-w-2xl mx-auto space-y-3">
                {[
                  {
                    q: '桥本有菜官方资料站是什么？',
                    a: '桥本有菜官方资料站是华语地区最全面的桥本有菜资讯平台，提供个人资料、作品资讯、写真集、最新动态等信息服务。',
                  },
                  {
                    q: '如何获取桥本有菜的最新动态？',
                    a: '您可以通过本站订阅最新资讯，或关注"最新动态"分类页面，我们会第一时间更新桥本有菜的相关信息。',
                  },
                  {
                    q: '桥本有菜有哪些代表作品？',
                    a: '桥本有菜的代表作品包括《花与少女》《东京恋歌》《星空下的约定》等影视作品，以及《花季》《夏の記憶》等写真集。',
                  },
                  {
                    q: '如何联系桥本有菜官方资料站？',
                    a: '您可以通过本站的联系表单发送留言，或发送邮件至 contact@hashimoto-arina.com，我们会尽快回复。',
                  },
                ].map((faq, i) => (
                  <details
                    key={i}
                    className="group bg-background-100 rounded-lg border border-background-200/70 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-200/50 transition-colors">
                      <span className="text-sm font-medium text-foreground-800">{faq.q}</span>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-500 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* Contact Form */}
            <section id="contact">
              <h2 className="text-2xl font-heading font-semibold text-foreground-900 mb-10 text-center">
                联系我们
              </h2>
              <div className="max-w-lg mx-auto">
                <div className="flex flex-col sm:flex-row gap-8 mb-8">
                  <div className="flex-1 bg-background-100 rounded-lg p-5 border border-background-200/70 text-center">
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <p className="text-xs text-foreground-400 mb-1">邮箱</p>
                    <p className="text-sm text-foreground-700">{aboutInfo.contact.email}</p>
                  </div>
                  <div className="flex-1 bg-background-100 rounded-lg p-5 border border-background-200/70 text-center">
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <p className="text-xs text-foreground-400 mb-1">地址</p>
                    <p className="text-sm text-foreground-700">{aboutInfo.contact.address}</p>
                  </div>
                </div>

                <form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                  data-readdy-form=""
                >
                  <div className="anti-spam-field">
                    <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的邮箱地址"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（不超过500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
                  </div>
                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    发送留言
                  </button>
                  {formStatus !== 'idle' && (
                    <p className={`text-sm text-center ${formStatus === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                      {formMessage}
                    </p>
                  )}
                </form>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}